import { combineEpics } from 'redux-observable'
import queries from 'sass/_queries.module.sass'
import { RootEpic } from '../../root'
import { exhaustMap, filter, map, mapTo, mergeMap, mergeMapTo, tap } from 'rxjs/operators'
import { isActionOf } from 'typesafe-actions'
import { userActions } from './user-actions'
import { getAll, remove } from 'es-cookie'
import { push } from 'connected-react-router'
import { ROUTES } from '../../../http/routes'
import { EMPTY, from, merge, of } from 'rxjs'
import { apiRequests } from '../../../http/config'
import { errorToast, toastFailedResponse } from '../../../components/controls/Toaster/Toaster'
import { pick, projectDeclination } from '../../../utils/utils'
import { store } from '../../store'
import { batch } from 'react-redux'
import { ProjectStatus } from '../../../responses/projects'
import { RefIntersection } from '../../../responses/refs/refs'
import { removeItem } from 'fp-ts-local-storage'
import { io } from 'fp-ts/IO'
import * as A from 'fp-ts/Array'
import { pipe } from 'fp-ts/function'
import LogRocket from 'logrocket'
import { projectListActions } from '../../pages/project-list'

const clearStorage = (keys: string[]) => {
    pipe(
        keys,
        A.map(removeItem),
        A.sequence(io)
    )()
}

const login: RootEpic = action$ => action$.pipe(
    filter(isActionOf(userActions.login.request)),
    mergeMap(({ payload }) => apiRequests.users.login({ data: payload })),
    mergeMap(({ response }) => {
        if (response.success) {
            window.local_token = `Bearer ${response.token}`
            //
            return from([
                userActions.login.success('Bearer ' + response.token),
                push(ROUTES.ROOT),
            ])
        } else {
            return of(userActions.login.failure(toastFailedResponse(response, 'Ошибка авторизации')))
        }
    }),
)

const logoutSequence = from([userActions.logout.success(), push(ROUTES.LOGIN)])
const info: RootEpic = action$ => action$.pipe(
    filter(isActionOf(userActions.info.request)),
    mergeMap(() => apiRequests.users.getUserInfo({})),
    mergeMap(({ response }) => {
        if (response.success) {
            LogRocket.identify(response.row.id.toString(), pick(response.row, 'name', 'description'))
            return of(userActions.info.success(response.row))
        } else {
            return merge(
                of(userActions.info.failure(toastFailedResponse(response, 'Ошибка получения данных о пользователе'))),
                logoutSequence,
            )
        }
    }),
)

const logout: RootEpic = action$ => action$.pipe(
    filter(isActionOf(userActions.logout.request)),
    mergeMap(() => apiRequests.users.logout({})),
    mergeMapTo(logoutSequence),
)

const afterLogout: RootEpic = action$ => action$.pipe(
    filter(isActionOf([userActions.logout.success, userActions.refreshToken.failure])),
    map(() => {
        const cookies = getAll()
        Object.keys(cookies).forEach(name => remove(name))
        clearStorage(['api-token'])
        window.local_token = null
        return push(ROUTES.LOGIN)
    })
)

const update: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(userActions.update.request)),
    mergeMap(({ payload }) => {
        const settings = state$.value.global.user.data?.settings || {}
        const data = {
            ...payload,
            settings: {
                ...settings,
                ...payload.settings,
            }
        }
        return apiRequests.users.currentUpdate({ data })
    }),
    map(({ response }) => response.success ?
        userActions.update.success(response.row) :
        userActions.update.failure(toastFailedResponse(response, 'Не удалось обновить данные пользователя'))
    ),
)

const infoAfterLogin: RootEpic = action$ => action$.pipe(
    filter(isActionOf([userActions.login.success, userActions.refreshToken.success])),
    tap(({ payload }) => localStorage.setItem('api-token', payload)),
    mapTo(userActions.info.request()),
)

const reload: RootEpic = action$ => action$.pipe(
    filter(isActionOf(userActions.reload)),
    tap(() => location.reload()),
    mergeMapTo(EMPTY),
)

const refreshToken: RootEpic = action$ => action$.pipe(
    filter(isActionOf(userActions.refreshToken.request)),
    exhaustMap(() => apiRequests.users.refreshToken({})),
    map(({ response }) => response.success ?
        userActions.refreshToken.success('Bearer ' + response.token) :
        userActions.refreshToken.failure(toastFailedResponse(response, 'Ошибка авторизации'))
    ),
)

const notifyAboutPausedProjects: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(userActions.info.success)),
    mergeMap(action => {
        const pausedProjects = action.payload.paused_projects
        const currentUser = state$.value.global.user.data
        if (pausedProjects > 0 && currentUser) {
            errorToast({
                timeout: 20000,
                content: `${projectDeclination(pausedProjects)} без активности очень долго, закройте ${pausedProjects > 1 ? 'их' : 'его'}. Либо верните в работу`,
                onClick: () => {
                    if (window.innerWidth > parseInt(queries.tabletBreakpoint)) {
                        batch(() => {
                            store.dispatch(projectListActions.clearFilters())
                            store.dispatch(projectListActions.updateFilters({
                                projectStatus: [ProjectStatus.PAUSED],
                                dropdowns: {manager: currentUser as unknown as RefIntersection}
                            }))
                        })
                    }
                },
            })
        }
        return EMPTY
    })
)

const inactiveLogout: RootEpic = action$ => action$.pipe(
    filter(isActionOf([userActions.update.success, userActions.info.success])),
    filter(action => !action.payload.active || action.payload.is_blocked),
    mergeMap(() => {
        errorToast('Вход прерван. Аккаунт заблокирован или неактивен')
        return logoutSequence
    })
)

export const userEpic = combineEpics(
    login, info, update, logout, afterLogout, infoAfterLogin, reload, refreshToken, notifyAboutPausedProjects,
    inactiveLogout,
)